import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
} from '@mlm/ui';
import { getPublicTenantConfig } from '../../lib/api';
import { useAuthStore } from '../../lib/auth-store';
import { useSaleStore } from '../../lib/cart-store';
import { afterSignIn, startConnectivityWatch } from '../../lib/offline/connectivity';
import { PACK_FRESH_MS, refreshPack } from '../../lib/offline/pack';
import { isValidPin, PIN_LENGTH_MAX, PIN_LENGTH_MIN } from '../../lib/offline/pin';
import {
  captureInstallPrompt,
  registerTillWorker,
  unregisterTillWorker,
} from '../../lib/offline/service-worker';
import { OFFLINE_TOKEN, useOfflineStore } from '../../lib/offline/state';
import {
  clearPack,
  currentPack,
  offlineUser,
  refreshPinProfile,
  setTillPin,
} from '../../lib/offline/store';

const PIN_LATER = 'mlm.cashier.pin.later';
const CHECK_EVERY_MS = 60_000;

/**
 * Keeps this till ready for the internet to go: installs the worker, keeps the
 * pack fresh, sends what is waiting, and asks a cashier for a till PIN once.
 * Only while the tenant has offline tills switched on. With it off, it clears
 * the device as soon as no sale is waiting.
 */
export function OfflineController() {
  const user = useAuthStore((s) => s.user);
  const token = useAuthStore((s) => s.token);
  const selectedBranchId = useSaleStore((s) => s.selectedBranchId);
  const online = useOfflineStore((s) => s.online);
  const pending = useOfflineStore((s) => s.pending);
  const configQuery = useQuery({
    queryKey: ['public-tenant-config'],
    queryFn: getPublicTenantConfig,
    staleTime: 60_000,
    retry: false,
  });
  const enabled = configQuery.data?.posOfflineEnabled === true;
  const realSession = !!token && token !== OFFLINE_TOKEN;
  const branchId = user?.branchId ?? selectedBranchId;

  useEffect(() => {
    captureInstallPrompt();
  }, []);

  useEffect(() => {
    if (!enabled) return;
    void registerTillWorker();
    void startConnectivityWatch();
  }, [enabled]);

  // Switched off. An unreachable config says nothing, so it needs a real answer.
  useEffect(() => {
    if (!configQuery.isSuccess || enabled || !online || !realSession || pending > 0) return;
    if (!useOfflineStore.getState().hasPack) return;
    void clearPack();
    void unregisterTillWorker();
  }, [configQuery.isSuccess, enabled, online, realSession, pending]);

  // Keep the pack fresh. It is what the till would price from if the internet
  // went right now.
  useEffect(() => {
    if (!enabled || !realSession || !online || !branchId) return;
    const refreshIfStale = () => {
      const pack = currentPack();
      const stale =
        !pack ||
        pack.branchId !== branchId ||
        Date.now() - new Date(pack.downloadedAt).getTime() > PACK_FRESH_MS;
      if (stale) void refreshPack(branchId);
    };
    refreshIfStale();
    const timer = window.setInterval(refreshIfStale, CHECK_EVERY_MS);
    return () => window.clearInterval(timer);
  }, [enabled, realSession, online, branchId]);

  // A real sign-in: send what is waiting, and keep this cashier's stored details fresh.
  useEffect(() => {
    if (!realSession || !user) return;
    void afterSignIn();
    void refreshPinProfile(user);
  }, [realSession, user]);

  return enabled && realSession && user ? <PinSetup /> : null;
}

/** Asked once per cashier per device, so they can unlock if the internet goes. */
function PinSetup() {
  const user = useAuthStore((s) => s.user)!;
  const [open, setOpen] = useState(false);
  const [pin, setPin] = useState('');
  const [again, setAgain] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const later = window.sessionStorage.getItem(PIN_LATER) === user.id;
    void offlineUser(user.id).then((row) => {
      if (!row && !later) setOpen(true);
    });
  }, [user.id]);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!isValidPin(pin)) {
      setError(`Use ${PIN_LENGTH_MIN} to ${PIN_LENGTH_MAX} digits.`);
      return;
    }
    if (pin !== again) {
      setError('The two PINs do not match.');
      return;
    }
    await setTillPin(user, pin);
    setPin('');
    setAgain('');
    setOpen(false);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          window.sessionStorage.setItem(PIN_LATER, user.id);
          setOpen(false);
        }
      }}
    >
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Set your till PIN</DialogTitle>
          <DialogDescription>
            If the internet goes down, this PIN lets you unlock this till and keep selling. It only
            works on this device.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <Input
            aria-label="New till PIN"
            type="password"
            inputMode="numeric"
            autoComplete="off"
            placeholder={`${PIN_LENGTH_MIN} to ${PIN_LENGTH_MAX} digits`}
            value={pin}
            onChange={(e) => setPin(e.target.value)}
          />
          <Input
            aria-label="Repeat till PIN"
            type="password"
            inputMode="numeric"
            autoComplete="off"
            placeholder="Repeat it"
            value={again}
            onChange={(e) => setAgain(e.target.value)}
          />
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
          <div className="flex gap-2">
            <Button type="submit" className="flex-1">
              Save PIN
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                window.sessionStorage.setItem(PIN_LATER, user.id);
                setOpen(false);
              }}
            >
              Later
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
